import { useState } from "react";

import { type Floor } from "@eisox/api/core-api";
import { ActionButton, Button, Heading, Modal } from "@eisox/design-system";
import { RoomIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import { m } from "~/paraglide/messages";
import type { EnhancedRoom } from "~/types/room.type";

import { areAllRoomsSelected, toggleAllRooms } from "../helpers";
import { ModalRoomsSelector } from "./modal-rooms-selector";
import { Room } from "./room";
import { RoomPlanPicker } from "./room-plan-picker";

const NAME = "RoomSelectorDialog";

interface RoomSelectorDialogProps extends Modal.RootProps {
  floors: Floor[];
  rooms: EnhancedRoom[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
}

const RoomsSelectorDialog: React.FC<RoomSelectorDialogProps> = ({
  floors,
  rooms,
  value = [],
  onValueChange,
  open: openProp,

  onOpenChange,
  defaultOpen = false,
  children,
  ...props
}) => {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange,
  });
  const [selectableRooms, setSelectableRooms] = useState<EnhancedRoom[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string[]>(value);

  const handleSelectRoom = (roomId: string) => {
    setSelectedRoomId(prev => (prev.includes(roomId) ? prev.filter(id => id !== roomId) : [...prev, roomId]));
  };
  const selectableRoomIds = selectableRooms.map(room => room.id);
  const allSelectableRoomsSelected = areAllRoomsSelected(selectedRoomId, selectableRoomIds);
  const handleToggleAll = () => {
    setSelectedRoomId(prev => toggleAllRooms(prev, selectableRoomIds));
  };
  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    setSelectableRooms([]);
  };
  const handleSubmit = () => {
    onValueChange?.(selectedRoomId);
    handleOpenChange(false);
  };

  const isDirty = value.length !== selectedRoomId.length || !selectedRoomId.every(r => value.includes(r));

  return (
    <Modal.Root open={open} onOpenChange={setOpen} {...props}>
      <Modal.Trigger asChild>{children}</Modal.Trigger>
      <Modal.Content className="flex h-[700px] w-[1200px] flex-col">
        <Modal.Header title={m.rooms_selector()} subtitle={m.select_rooms()} icon={<RoomIcon />}>
          <Modal.Close asChild>
            <ActionButton variant="cancel">{m.cancel()}</ActionButton>
          </Modal.Close>
          <ActionButton disabled={!isDirty} onClick={handleSubmit}>
            {m.select()}
          </ActionButton>
        </Modal.Header>
        <div className="min-h-0 flex flex-1 gap-[40px]">
          <div className="min-h-0 flex max-w-[400px] min-w-[200px] flex-1 flex-col gap-[10px]">
            <Heading size={4} weight={"medium"}>
              {m.rooms()}
            </Heading>
            <ModalRoomsSelector floors={floors} rooms={rooms} onFilteredRoomsChange={setSelectableRooms} />
            <Button className="self-start" disabled={selectableRooms.length === 0} onClick={handleToggleAll}>
              {allSelectableRoomsSelected ? m.unselect_all() : m.select_all()}
            </Button>
            <div className="min-h-0 flex flex-col gap-[5px] overflow-y-auto">
              {selectableRooms.map(room => (
                <Room
                  key={room.id}
                  multiple
                  name={room.name}
                  planName={room.floor.name}
                  selected={selectedRoomId.includes(room.id)}
                  onClick={() => handleSelectRoom(room.id)}
                />
              ))}
            </div>
          </div>
          <RoomPlanPicker
            className="flex-1"
            multiple
            floors={floors}
            rooms={rooms}
            value={selectedRoomId}
            onValueChange={setSelectedRoomId}
          />
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};

RoomsSelectorDialog.displayName = NAME;

export { RoomsSelectorDialog };
export type { RoomSelectorDialogProps };
